export interface KioskCapacityPass {
  id: string;
  class_period_id?: string | null;
  period_label?: string | null;
  status: string;
  uses_restroom_capacity?: number | boolean;
  restroom_max_out?: number | null;
}

export interface KioskCapacityChip {
  periodId: string;
  periodLabel: string;
  available: number;
}

/**
 * Build capacity chips only for periods that currently have a student out on
 * a restroom-capacity pass. READY passes reserve an opening, while emergency
 * and accommodation passes remain ACTIVE even if they push a period above its
 * maximum; available slots are therefore clamped at zero.
 */
export function kioskCapacityChips(
  passes: KioskCapacityPass[],
  fallbackMaximum: number,
): KioskCapacityChip[] {
  const readyByPeriod = new Map<string, number>();
  for (const pass of passes) {
    if (!pass.uses_restroom_capacity || pass.status !== "READY") continue;
    const periodId = pass.class_period_id ?? "UNASSIGNED";
    readyByPeriod.set(periodId, (readyByPeriod.get(periodId) ?? 0) + 1);
  }

  const activeByPeriod = new Map<string, {
    periodId: string;
    periodLabel: string;
    maximum: number;
    active: number;
  }>();
  for (const pass of passes) {
    if (!pass.uses_restroom_capacity || pass.status !== "ACTIVE") continue;
    const periodId = pass.class_period_id ?? "UNASSIGNED";
    const existing = activeByPeriod.get(periodId);
    if (existing) {
      existing.active += 1;
      continue;
    }
    const configuredMaximum = Number(pass.restroom_max_out ?? fallbackMaximum);
    activeByPeriod.set(periodId, {
      periodId,
      periodLabel: pass.period_label?.trim() || "Unassigned period",
      maximum: Number.isFinite(configuredMaximum) ? Math.max(0, configuredMaximum) : 0,
      active: 1,
    });
  }

  return [...activeByPeriod.values()]
    .map((period) => ({
      periodId: period.periodId,
      periodLabel: period.periodLabel,
      available: Math.max(
        0,
        period.maximum - period.active - (readyByPeriod.get(period.periodId) ?? 0),
      ),
    }))
    .sort((left, right) =>
      left.periodLabel.localeCompare(right.periodLabel, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
}
